import { FilterFilled, SearchOutlined } from "@ant-design/icons";
import { Collapse, Tag, Typography } from "antd";
import { useEffect, useState, useContext } from "react";
import CustomFormItemsHorizontal from "../common/CustomFormItemsHorizontal";
import useFetch from "../../hooks/useFetch";
import { IndexContext } from "../../context/IndexContext";
import dayjs from "dayjs";

const { Text } = Typography;

export default function ReportFilter({
  form,
  applyFilter,
  pickerType,
  setPickerType,
  setDeviceLabelSelected,
}) {
  const [activeKey, setActiveKey] = useState(1);

  const [fetchWrapper] = useFetch();

  // report type selection, the value is the picker type for DatePicker
  const reportTypeOptions = [
    { label: "Half-Hour Report", value: "half_and_hour" },
    { label: "Daily Report", value: "date" },
    { label: "Weekly Report", value: "week" },
    { label: "Monthly Report", value: "month" },
    { label: "Yearly Report", value: "year" },
  ];

  // loading for device list selection (load while it's still getting device list from backend)
  const [loadingDeviceList, setLoadingDeviceList] = useState(false);

  const deviceViewList = [
    { value: 1, label: "Area" },
    { value: 2, label: "SEU" },
    { value: 3, label: "Device" },
  ];
  const [deviceViewSelected, setDeviceViewSelected] = useState(null);

  // device list and its selection
  const [deviceList, setDeviceList] = useState([]);
  const [deviceSelected, setDeviceSelected] = useState(null);

  // site commissioning date
  const [siteCommissioningDate, setSiteCommissioningDate] = useState(dayjs());

  // load index context
  const { siteSelected } = useContext(IndexContext);

  /* API to get device list */
  function getDeviceList() {
    setDeviceList([]);
    setLoadingDeviceList(true);
    fetchWrapper({
      endpoint_url: "trending/getDeviceList",
      body: {
        site_id: siteSelected,
        device_view_id: deviceViewSelected,
      },
      onSuccess: (fetch_output) => {
        if (fetch_output.status === true) {
          setDeviceList(fetch_output.device_list);
        }
        setLoadingDeviceList(false);
      },
    });
  }

  /* API to get site commissioning date */
  function getSiteCommissioningDate() {
    fetchWrapper({
      endpoint_url: "trending/getSiteCommissioningDate",
      body: {
        site_id: siteSelected,
      },
      onSuccess: (fetch_output) => {
        if (fetch_output.status === true) {
          setSiteCommissioningDate(fetch_output.site_commissioning_date);
        }
      },
    });
  }

  useEffect(() => {
    if (deviceViewSelected && siteSelected) {
      getDeviceList();
    }
  }, [siteSelected, deviceViewSelected]);

  useEffect(() => {
    if (siteSelected) {
      getSiteCommissioningDate();
    }

    form.setFieldValue("report_type", null);
    setPickerType(null);
    form.setFieldValue("start", null);
    form.setFieldValue("end", null);

    setDeviceViewSelected(null);
    form.setFieldValue("device_view", null);
    setDeviceSelected(null);
    form.setFieldValue("device", []);
  }, [siteSelected]);

  const items = [
    {
      key: 1,
      label: (
        <>
          <FilterFilled />
          <Text strong className="ml-2">
            FILTER
          </Text>
        </>
      ),
      children: (
        <>
          <CustomFormItemsHorizontal
            submitIcon={<SearchOutlined />}
            submitText={"Search"}
            onFinish={applyFilter}
            form={form}
            config={[
              {
                name: "report_type",
                label: "Report Type",
                type: "select",
                options: reportTypeOptions,
                onChange: (value) => {
                  setPickerType(value);
                },
                rules: [
                  {
                    required: true,
                    message: "Please select which report type you want.",
                  },
                ],
              },
              {
                name: "start",
                label: "From",
                type: "datetime",
                picker: pickerType ? pickerType : "date",
                disabled: !pickerType,
                minDate: dayjs(siteCommissioningDate, "YYYY-MM-DD"),
                maxDate: dayjs(),
                isDateOnly: true,
                tooltip:
                  "Please select report type first before date selection.",
                rules: [
                  {
                    required: true,
                    message: "Please select start date.",
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (getFieldValue("end")) {
                        if (getFieldValue("report_type") === "week") {
                          const startWeek = value ? value.week() : null;
                          const endWeek = getFieldValue("end")
                            ? getFieldValue("end").week()
                            : null;

                          if (endWeek && startWeek > endWeek) {
                            return Promise.reject(
                              new Error(
                                "Start date must be the same as or earlier than end date."
                              )
                            );
                          } else {
                            return Promise.resolve();
                          }
                        } else {
                          if (getFieldValue("end") < value) {
                            return Promise.reject(
                              new Error(
                                "Start date must be same as or earlier than end date."
                              )
                            );
                          } else {
                            return Promise.resolve();
                          }
                        }
                      }
                    },
                  }),
                ],
              },
              {
                name: "end",
                label: "To",
                type: "datetime",
                picker: pickerType ? pickerType : "date",
                disabled: !pickerType,
                minDate: dayjs(siteCommissioningDate, "YYYY-MM-DD"),
                maxDate: dayjs(),
                isDateOnly: true,
                tooltip:
                  "Please select report type first before date selection.",
                rules: [
                  {
                    required: true,
                    message: "Please select end date.",
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (getFieldValue("start")) {
                        if (getFieldValue("report_type") === "week") {
                          const startWeek = getFieldValue("start")
                            ? getFieldValue("start").week()
                            : null;
                          const endWeek = value ? value.week() : null;

                          if (startWeek && startWeek > endWeek) {
                            return Promise.reject(
                              new Error(
                                "End date must be the same as or later than start date."
                              )
                            );
                          } else {
                            return Promise.resolve();
                          }
                        } else {
                          if (getFieldValue("start") > value) {
                            return Promise.reject(
                              new Error(
                                "End date must be same as or later than start date."
                              )
                            );
                          } else {
                            return Promise.resolve();
                          }
                        }
                      }
                    },
                  }),
                ],
              },
              {
                name: "device_view",
                label: "View Device By",
                type: "select",
                options: deviceViewList,
                onChange: (value) => {
                  setDeviceViewSelected(value);
                  form.setFieldValue("device", []);
                },
                rules: [
                  {
                    required: true,
                    message: "Please select how do you want to view device.",
                  },
                ],
              },
              {
                name: "device",
                label: "Device",
                type: "tree",
                colspan: { span: 24 },
                multiple: true,
                tooltip: deviceViewSelected
                  ? deviceList.length === 0
                    ? "There is no device for the selected view."
                    : "'*' means the device is the source of area / SEU. \
                    If you select area / SEU, the sum of the devices will be calculated. \
                    If the area / SEU selection is disabled, it means none of the devices is the source for the area / SEU."
                  : "Please select how to you want to view device.",
                onChange: (value, label) => {
                  setDeviceSelected(value);
                  setDeviceLabelSelected(label);
                },
                rules: [{ required: true, message: "Please select device." }],
                showCheckedStrategy: "all",
                options: deviceList,
                disabled: siteSelected && deviceViewSelected ? false : true,
                loading: loadingDeviceList,
                tagRender: ({ label, onClose, closable }) => (
                  <Tag
                    color="var(--ant-color-primary)"
                    closable={closable}
                    onClose={onClose}
                    style={{
                      marginRight: 3,
                    }}
                  >
                    <Text style={{ color: "white" }}>{label}</Text>
                  </Tag>
                ),
              },
            ]}
          />
        </>
      ),
    },
  ];

  return (
    <Collapse
      expandIconPosition="end"
      defaultActiveKey={activeKey}
      size="middle"
      onChange={(key) => setActiveKey(key)}
      items={items}
    ></Collapse>
  );
}
